/**
 * Hall/NIH-inspired independent approximation of published dynamic weight models, not a copy:
 * expenditure adapts with weight and tissue energy changes with remaining excess weight.
 */
import { ABSOLUTE_MIN_KCAL, ACTIVITY_MULTIPLIERS, ADAPTIVE_THERMOGENESIS_KCAL_PER_KG, ALGO_VERSIONS, DEFAULT_ACTIVITY_MULTIPLIER, MIN_KCAL_BMR_FRACTION_LOSE, MS_PER_WEEK, SIM_MAX_WEEKS, STEPS_ADJUSTMENT_MAX, STEPS_MULTIPLIER_PER_1000, STEPS_REFERENCE, TIMELINE_RANGE_FRACTION, TISSUE_KCAL_PER_KG } from "./constants";
import { weeklyRateFromAggression } from "./goals";
import { calculateBmi, calculateBmr } from "./metabolism";
import { ENGINE_VERSION, type EngineMeta, type EngineProfile, type EngineResult, type ForecastPoint, type ForecastValue, type GoalDurationValue } from "./types";

type SimulationRun = {
  readonly points: readonly ForecastPoint[]; readonly weeks: number; readonly reachedGoal: boolean;
  readonly initialTargetKcal: number; readonly requestedWeeklyChangeKg: number;
  readonly effectiveWeeklyChangeKg: number; readonly constrainedWeeks: number;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function roundOne(value: number): number {
  return Math.round(value * 10) / 10;
}

function activityFactors(profile: EngineProfile): { readonly multiplier: number; readonly stepsAdjustment: number } {
  const multiplier =
    ACTIVITY_MULTIPLIERS[profile.activityLevel ?? ""] ?? DEFAULT_ACTIVITY_MULTIPLIER;
  const steps = profile.avgDailySteps;
  const stepsAdjustment =
    steps !== undefined && steps > 0
      ? clamp(
          ((steps - STEPS_REFERENCE) / 1000) * STEPS_MULTIPLIER_PER_1000,
          -STEPS_ADJUSTMENT_MAX,
          STEPS_ADJUSTMENT_MAX,
        )
      : 0;
  return { multiplier, stepsAdjustment };
}

function bmrAtWeight(profile: EngineProfile, weightKg: number, now: number): number | null {
  return calculateBmr({ ...profile, weightKg }, now)?.value.bmrKcal ?? null;
}

function crossedGoal(goalType: string | undefined, weightKg: number, targetWeightKg: number): boolean {
  if (goalType === "lose") return weightKg <= targetWeightKg;
  if (goalType === "gain" || goalType === "muscle") return weightKg >= targetWeightKg;
  return true;
}

function simulate(
  profile: EngineProfile,
  initialExpenditureKcal: number,
  maxWeeks: number,
  now: number,
  pointLimit: number,
): SimulationRun {
  const startingWeightKg = profile.weightKg ?? 70;
  const targetWeightKg = profile.targetWeightKg ?? startingWeightKg;
  const factors = activityFactors(profile);
  const startingBmr = bmrAtWeight(profile, startingWeightKg, now);
  const modelMultiplier =
    startingBmr === null
      ? factors.multiplier + factors.stepsAdjustment
      : initialExpenditureKcal / startingBmr;
  let currentWeightKg = startingWeightKg;
  let constrainedWeeks = 0;
  let initialTargetKcal = Math.round(initialExpenditureKcal / 10) * 10;
  let requestedWeeklyChangeKg = 0;
  let effectiveWeeklyChangeKg = 0;
  const points: ForecastPoint[] = [];

  for (let week = 1; week <= maxWeeks; week += 1) {
    if (crossedGoal(profile.goalType, currentWeightKg, targetWeightKg)) {
      return {
        points,
        weeks: week - 1,
        reachedGoal: true,
        initialTargetKcal,
        requestedWeeklyChangeKg,
        effectiveWeeklyChangeKg,
        constrainedWeeks,
      };
    }
    const bmr = bmrAtWeight(profile, currentWeightKg, now);
    if (bmr === null) break;
    const kgLost = Math.max(0, startingWeightKg - currentWeightKg);
    const estimatedExpenditure = bmr * modelMultiplier - ADAPTIVE_THERMOGENESIS_KCAL_PER_KG * kgLost;
    const expenditure = Math.max(bmr * 0.9, estimatedExpenditure);
    const bmi = calculateBmi({ ...profile, weightKg: currentWeightKg }, now)?.value.bmi;
    const rate = weeklyRateFromAggression(
      profile.goalType,
      profile.weightLossPace,
      currentWeightKg,
      targetWeightKg,
      bmi,
    );
    const rateMagnitude = Math.abs(rate.cappedKg);
    const requestedDeficit = (rateMagnitude * TISSUE_KCAL_PER_KG) / 7;
    const intakeFloor = profile.goalType === "lose"
      ? Math.max(ABSOLUTE_MIN_KCAL, MIN_KCAL_BMR_FRACTION_LOSE * bmr)
      : ABSOLUTE_MIN_KCAL;
    const achievableDeficit =
      profile.goalType === "lose"
        ? Math.min(requestedDeficit, Math.max(0, expenditure - intakeFloor))
        : requestedDeficit;
    if (achievableDeficit + 0.01 < requestedDeficit) constrainedWeeks += 1;
    const intake = profile.goalType === "lose"
      ? expenditure - achievableDeficit
      : expenditure + achievableDeficit;
    if (week === 1) {
      initialTargetKcal = Math.round(intake / 10) * 10;
      requestedWeeklyChangeKg = rate.cappedKg;
    }
    const excessKg = Math.abs(currentWeightKg - targetWeightKg);
    const tissueEnergyPerKg =
      TISSUE_KCAL_PER_KG * (0.75 + 0.25 * Math.min(1, excessKg / 10));
    const weeklyDeltaKg = (achievableDeficit * 7) / tissueEnergyPerKg;
    const signedDeltaKg = profile.goalType === "lose" ? -weeklyDeltaKg : weeklyDeltaKg;
    let nextWeightKg = currentWeightKg + signedDeltaKg;
    if (crossedGoal(profile.goalType, nextWeightKg, targetWeightKg)) nextWeightKg = targetWeightKg;
    effectiveWeeklyChangeKg = week === 1 ? signedDeltaKg : effectiveWeeklyChangeKg;
    currentWeightKg = nextWeightKg;
    if (points.length < pointLimit) {
      points.push({
        weekIndex: week,
        date: now + week * MS_PER_WEEK,
        projectedWeightKg: roundOne(currentWeightKg),
        intakeKcal: Math.round(intake),
        expenditureKcal: Math.round(expenditure),
      });
    }
  }

  return {
    points,
    weeks: maxWeeks,
    reachedGoal: crossedGoal(profile.goalType, currentWeightKg, targetWeightKg),
    initialTargetKcal,
    requestedWeeklyChangeKg,
    effectiveWeeklyChangeKg,
    constrainedWeeks,
  };
}

function restrictionReason(profile: EngineProfile, now: number): string | null {
  if ((profile.safetyFlags?.length ?? 0) > 0) {
    return "Safety flags require professional guidance before projecting a weight-change timeline.";
  }
  const bmi = calculateBmi(profile, now)?.value.bmi;
  if (profile.goalType === "lose" && bmi !== undefined && bmi < 18.5) {
    return "An underweight BMI makes automated weight-loss projection inappropriate.";
  }
  return null;
}

function createMeta(
  kind: "goalDuration" | "forecast",
  now: number,
  inputs: EngineMeta["inputs"],
): EngineMeta {
  return {
    kind,
    engineVersion: ENGINE_VERSION,
    algorithmVersion: ALGO_VERSIONS[kind],
    computedAt: now,
    estimateKind: "initial",
    confidence: "low",
    explanation: "This independent dynamic approximation updates resting energy, expenditure, and tissue energy as projected weight changes; it is not a copy of a published model.",
    inputs,
  };
}

export function simulateGoalTimeline(
  profile: EngineProfile,
  initialExpenditureKcal: number,
  now = Date.now(),
): EngineResult<GoalDurationValue> {
  const restriction = restrictionReason(profile, now);
  if (restriction !== null) {
    return {
      value: {
        weeksToGoal: 0,
        weeksMin: 0,
        weeksMax: 0,
        goalDateMin: now,
        goalDateMax: now,
        initialTargetKcal: Math.round(initialExpenditureKcal / 10) * 10,
        requestedWeeklyChangeKg: 0,
        effectiveWeeklyChangeKg: 0,
        extendedForSafety: false,
        infeasible: true,
        notes: [restriction],
      },
      meta: createMeta("goalDuration", now, {
        initialExpenditureKcal: Math.round(initialExpenditureKcal),
        goalType: profile.goalType ?? "unknown",
        safetyFlagCount: profile.safetyFlags?.length ?? 0,
      }),
    };
  }

  const run = simulate(profile, initialExpenditureKcal, SIM_MAX_WEEKS, now, 0);
  const weeksMin = Math.round(run.weeks * (1 - TIMELINE_RANGE_FRACTION));
  const weeksMax = Math.round(run.weeks * (1 + TIMELINE_RANGE_FRACTION));
  const extendedForSafety = run.weeks > 0 && run.constrainedWeeks / run.weeks > 0.25;
  const notes = ["Progress slows near your goal as your body needs fewer calories."];
  if (extendedForSafety) notes.push("The timeline is extended when the requested deficit would push intake below safety limits.");
  if (!run.reachedGoal) notes.push("The goal was not reached within the maximum projection of 520 weeks.");

  return {
    value: {
      weeksToGoal: run.weeks,
      weeksMin,
      weeksMax,
      goalDateMin: now + weeksMin * MS_PER_WEEK,
      goalDateMax: now + weeksMax * MS_PER_WEEK,
      initialTargetKcal: run.initialTargetKcal,
      requestedWeeklyChangeKg: roundOne(run.requestedWeeklyChangeKg),
      effectiveWeeklyChangeKg: roundOne(run.effectiveWeeklyChangeKg),
      extendedForSafety,
      infeasible: !run.reachedGoal,
      notes,
    },
    meta: createMeta("goalDuration", now, {
      weightKg: profile.weightKg ?? null,
      targetWeightKg: profile.targetWeightKg ?? null,
      goalType: profile.goalType ?? "unknown",
      pace: profile.weightLossPace ?? "steady",
      initialExpenditureKcal: Math.round(initialExpenditureKcal),
    }),
  };
}

export function forecastWeightCurve(
  profile: EngineProfile,
  initialExpenditureKcal: number,
  weeks: number,
  now = Date.now(),
): EngineResult<ForecastValue> {
  const restriction = restrictionReason(profile, now);
  const maxWeeks = Math.max(0, Math.min(SIM_MAX_WEEKS, Math.floor(weeks)));
  const run =
    restriction === null
      ? simulate(profile, initialExpenditureKcal, maxWeeks, now, 104)
      : {
          points: [],
          weeks: 0,
          reachedGoal: false,
          initialTargetKcal: Math.round(initialExpenditureKcal / 10) * 10,
          requestedWeeklyChangeKg: 0,
          effectiveWeeklyChangeKg: 0,
          constrainedWeeks: 0,
        };
  const weeksToGoal = run.reachedGoal ? run.weeks : null;

  return {
    value: {
      points: run.points,
      projectedGoalDate: weeksToGoal === null ? null : now + weeksToGoal * MS_PER_WEEK,
      weeksToGoal,
    },
    meta: createMeta("forecast", now, {
      weightKg: profile.weightKg ?? null,
      targetWeightKg: profile.targetWeightKg ?? null,
      requestedWeeks: weeks,
      simulatedWeeks: maxWeeks,
      initialExpenditureKcal: Math.round(initialExpenditureKcal),
      restricted: restriction !== null,
    }),
  };
}
